import posthog from "posthog-js";
import { Common } from "../../../utils";
import { axiosBasic, getUserInfo } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { ACCRUAL_START_DATE_VALUES } from "../../../utils/constants";
import {
  IS_LOADING,
  USERS_LOADING,
  USER_DATA_LOADING,
  ADMIN_ROLES,
  OFFICES,
  USERS,
  APPROVERS,
  TOGGLE_USER_STATUS,
  UPDATE_PROFILE_IMAGE,
  DEPARTMENTS,
} from "./userActionsTypes";
import { UPDATE_PAGING } from "../../common/pagination/paginationActionTypes";
import {
  buildAdditionalProfilePayload,
  buildTeamIdsPayload,
} from "./userFormHelpers";

const { Account, Office, LeaveType, AppUser, Department, LeaveAccrual } = config.REST_API;

// Build userInput for PostHog, excluding password and other sensitive fields
const safeUserInput = (values, excludeKeys = ["password", "confirmPassword", "imageUrl", "email"]) => {
  if (!values || typeof values !== "object") return undefined;
  const out = {};
  const keys = excludeKeys.map((k) => k.toLowerCase());
  Object.keys(values).forEach((key) => {
    if (keys.includes(key.toLowerCase())) return;
    const v = values[key];
    if (Array.isArray(v)) out[key] = `[${v.length} items]`;
    else if (v != null && typeof v === "object" && !(v instanceof Date)) out[key] = "(object)";
    else out[key] = v;
  });
  return Object.keys(out).length ? out : undefined;
};

export const isLoading = (value) => ({
  type: IS_LOADING,
  payload: value,
});

export const setUsersLoading = (value) => ({
  type: USERS_LOADING,
  payload: value,
});

export const setUserDataLoading = (value) => ({
  type: USER_DATA_LOADING,
  payload: value,
});

export const users = (users, context) => ({
  type: USERS,
  payload: users,
  context,
});

export const onGetUsers = (params, currentPage) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { officeId: params.officeId, departmentId: params.departmentId, page: params.page, limit: params.limit, isActive: params.isActive, currentPage };
  dispatch(setUsersLoading(true));
  // Ensure start parameter is correctly calculated (0-based index)
  const start = params.start !== undefined ? params.start : (params.page - 1) * params.limit;

  return new axiosBasic
    .get(`${AppUser.GetUsers}?officeId=${params.officeId}&departmentId=${params.departmentId || 0}&start=${start}&page=${params.page}&take=${params.limit}&isActive=${params.isActive}`)
    .then((response) => {
      const { success, message, data } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetUsers", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (message?.message || message),
        });
      }
      if (success) {
        // Ensure we have a valid total count - check multiple possible response structures
        // IMPORTANT: Don't use data.length as total for paginated results - it only represents current page
        const total = response.data.total !== undefined && response.data.total !== null
          ? response.data.total
          : response.data.totalCount !== undefined && response.data.totalCount !== null
            ? response.data.totalCount
            : 0; // Don't fallback to data.length - it's only the current page's data

        // Ensure data is an array - check multiple possible response structures
        const usersData = Array.isArray(data)
          ? data
          : Array.isArray(response.data.data)
            ? response.data.data
            : Array.isArray(response.data.users)
              ? response.data.users
              : [];

        dispatch({
          type: UPDATE_PAGING,
          payload: {
            data: {
              total: total,
              currentPage: currentPage,
              pageLimit: params.limit,
            },
            response: usersData,
          },
        });
        // Dispatch users data with context so we can skip refetch when navigating back with same filters
        const listContext = {
          limit: params.limit,
          isActive: params.isActive,
          officeId: params.officeId,
          departmentId: params.departmentId || 0,
        };
        dispatch(users(usersData, listContext));
      } else {
        Common.showErrorMessage(message?.message || 'Failed to fetch users');
        dispatch(users([], null));
        dispatch(setUsersLoading(false));
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetUsers", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message?.errmsg || error?.message || "Request failed",
        });
      }
      Common.showErrorMessage(error.response?.data?.message?.errmsg || error.message || 'Failed to fetch users');
      dispatch(users([], null));
      dispatch(setUsersLoading(false));
    });
};

export const onSearchUsers = (params, currentPage) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { officeId: params.officeId, departmentId: params.departmentId, page: params.page, limit: params.limit, isActive: params.isActive, currentPage, hasSearchText: !!(params.searchText && String(params.searchText).trim()) };
  dispatch(setUsersLoading(true));
  const searchQuery = params.searchText ? `query=${encodeURIComponent(params.searchText)}&` : '';
  // Ensure start parameter is correctly calculated (0-based index)
  const start = params.start !== undefined ? params.start : (params.page - 1) * params.limit;

  return new axiosBasic
    .get(`${AppUser.Search}?${searchQuery}officeId=${params.officeId}&departmentId=${params.departmentId || 0}&start=${start}&page=${params.page}&take=${params.limit}&isActive=${params.isActive}`)
    .then((response) => {
      const { success, message, data } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onSearchUsers", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (message?.message || message),
        });
      }
      if (success) {
        // Ensure we have a valid total count - check multiple possible response structures
        // IMPORTANT: Don't use data.length as total for paginated results - it only represents current page
        const total = response.data.total !== undefined && response.data.total !== null
          ? response.data.total
          : response.data.totalCount !== undefined && response.data.totalCount !== null
            ? response.data.totalCount
            : 0; // Don't fallback to data.length - it's only the current page's data

        // Ensure data is an array - check multiple possible response structures
        const usersData = Array.isArray(data)
          ? data
          : Array.isArray(response.data.data)
            ? response.data.data
            : Array.isArray(response.data.users)
              ? response.data.users
              : [];

        dispatch({
          type: UPDATE_PAGING,
          payload: {
            data: {
              total: total,
              currentPage: currentPage,
              pageLimit: params.limit,
            },
            response: usersData,
          },
        });
        // Search results: pass null context so we don't match initial-load skip-refetch
        dispatch(users(usersData, null));
      } else {
        Common.showErrorMessage(message?.message || 'Failed to search users');
        dispatch(users([], null));
        dispatch(setUsersLoading(false));
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onSearchUsers", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message?.errmsg || error?.message || "Request failed",
        });
      }
      Common.showErrorMessage(error.response?.data?.message?.errmsg || error.message || 'Failed to search users');
      dispatch(users([], null));
      dispatch(setUsersLoading(false));
    });
};

export const onGetUser = (id, mode) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { id, mode };
  dispatch(setUserDataLoading(true));
  if (mode === 'edit') {
    return new axiosBasic
      .get(`${AppUser.GetUser}?userId=${id}`)
      .then((response) => {
        const { success, message, data } = response.data;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("onGetUser", {
            type: success ? "info" : "exception",
            userId,
            success,
            userInput,
            reason: success ? undefined : (message?.message || message),
          });
        }
        if (success) {
          if (data?.userLeaveSettings && Array.isArray(data.userLeaveSettings)) {
            const mappedSettings = data.userLeaveSettings.map(setting => ({
              ...setting,
              leaveTypeId: setting.leaveTypeId || (setting.leaveType ? setting.leaveType.id : undefined),
              leaveBalanceId: setting.leaveBalanceId || setting.id, // Use leaveBalanceId or fallback to id
              // Preserve custom accrual settings from backend
              isCustomAccrualLeave: setting.isCustomAccrualLeave || false,
              enableAccrual: setting.enableAccrual !== undefined ? setting.enableAccrual : (setting.isCustomAccrualLeave ? true : undefined),
              customAccrualRate: setting.customAccrualRate,
              customAccrualFrequency: setting.customAccrualFrequency,
              customAccrualCap: setting.customAccrualCap,
              accrualStartFrequency: setting.accrualStartFrequency,
              accrualEndDate: setting.accrualEndDate,
              // Map backend field names if needed
              totalAllowance: setting.totalAllowance,
              usedLeaves: setting.usedLeaves,
              remainingLeaves: setting.remainingLeaves,
            }));
            dispatch(users({ ...data, userLeaveSettings: mappedSettings }));
          } else if (Array.isArray(data?.leaveBalance) && data?.leaveBalance?.length > 0) {
            console.log("🔍 Processing existing leave balance:", data.leaveBalance);
            // When mapping leave balances (existing leaves)
            const existingLeaves = data.leaveBalance.map((leave) => {
              return {
                leaveTypeId: leave.leaveTypesId,
                leaveBalanceId: leave.leaveBalanceId, // Use leaveBalanceId from backend
                name: leave.leaveTypes?.name || "Unknown",
                days: leave.days || 0,
                hours: leave.hours || 0,
                type: leave.leaveDivision === 0 ? 'Days' : 'Hours',
                value: leave.leaveDivision === 0 ? leave.days : leave.hours,
                active: leave.active,
              };
            });
            console.log("🔍 Processed existing leaves:", existingLeaves);
            console.log("🔍 All leave types:", data.allLeaveTypes);

            const userLeaveSettings = data.allLeaveTypes.map((leaveType) => {
              const existing = existingLeaves.find((l) => l.leaveTypeId === leaveType.id);
              console.log(`🔍 Matching leaveType ${leaveType.id} with existing:`, existing);

              return existing ? {
                ...existing,
                leaveTypeId: leaveType.id, // Use leaveType.id as the main id
                active: leaveType.active, // Add active property for existing leaves
              } : {
                leaveTypeId: leaveType.id,
                leaveBalanceId: leaveType.leaveBalanceId,
                name: leaveType.name,
                value: 0, // Use 0 instead of empty string
                type: 'Days', // or 'Hours' as default
                active: leaveType.active, // Add active property for new leaves
              };
            });
            console.log("🔍 Final userLeaveSettings:", userLeaveSettings);

            const updatedUser = {
              ...data,
              userLeaveSettings, // dynamically added field
            };
            dispatch(users(updatedUser));
          } else {
            // Even when there's no leave balance, set up userLeaveSettings from allLeaveTypes
            const userLeaveSettings = data.allLeaveTypes ? data.allLeaveTypes.map((leaveType) => ({
              leaveTypeId: leaveType.id,
              id: leaveType.id,
              name: leaveType.name,
              value: 0, // Use 0 instead of empty string
              type: 'Days', // Default
              active: leaveType.active,
            })) : [];

            const updatedUser = {
              ...data,
              userLeaveSettings, // dynamically added field
            };
            dispatch(users(updatedUser));
          }

        } else {
          Common.showErrorMessage(message.message);
        }
        dispatch(setUserDataLoading(false));
      })
      .catch((error) => {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("onGetUser", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message?.errmsg || error?.message || "Request failed",
          });
        }
        Common.showErrorMessage(error.response.data.message.errmsg);
        dispatch(setUserDataLoading(false));
      });
  } else {
    axiosBasic
      .get(LeaveType.GetActiveLeaveTypes)
      .then((response) => {
        const { success, data, total } = response.data;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("onGetUser", {
            type: success ? "info" : "exception",
            userId,
            success: !!success,
            userInput,
          });
        }
        if (success && total > 0) {

          const userLeaveSettings = data.map((leaveType) => ({
            leaveTypeId: leaveType.id,
            id: leaveType.id,
            name: leaveType.name,
            value: leaveType.defaultLeaveCount || 0, // Use defaultLeaveCount from LeaveType API
            type: 'Days', // Default
            active: leaveType.active,
            deducted: leaveType.deducted !== undefined ? leaveType.deducted : (leaveType.takeFromAllowance !== undefined ? leaveType.takeFromAllowance : false),
            defaultLeaveCount: leaveType.defaultLeaveCount || 0,
            icon: leaveType.icon || leaveType.selectOption || 'no-icon',
            selectOption: leaveType.selectOption || leaveType.icon || 'no-icon',
            isDefaultLeave: leaveType.isDefaultLeave || false,
            isMaxAbsent: leaveType.isMaxAbsent || leaveType.maxAbsent || false,
            maxAbsent: leaveType.maxAbsent || leaveType.isMaxAbsent || false,
            leaveDivision: leaveType.leaveDivision || 0,
            requiresApproval: leaveType.requiresApproval || false,
            color: leaveType.color || '#00B4D8',
            allowCarryForward: leaveType.allowCarryForward || false,
            allowCarryForwardExpiry: leaveType.allowCarryForwardExpiry || false,
            carryForwardExpiryMonths: leaveType.carryForwardExpiryMonths || 0,
            carryForwardFixedAmount: leaveType.carryForwardFixedAmount || 0,
            carryForwardPercentage: leaveType.carryForwardPercentage || 0,
            carryForwardPolicy: leaveType.carryForwardPolicy || 'Fixed',
            maxCarryForward: leaveType.maxCarryForward || 0,
            leaveYearStart: leaveType.leaveYearStart || null,
            companyId: leaveType.companyId || null,
            // Accrual fields
            accrualMethod: leaveType.accrualMethod || 'None',
            accrualRate: leaveType.accrualRate || 0,
            accrualFrequency: leaveType.accrualFrequency || null,
            maxAccrualBalance: leaveType.maxAccrualBalance || 0,
            enableProration: leaveType.enableProration !== undefined ? leaveType.enableProration : true,
            accrualStartType: leaveType.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START,
            tieredRates: leaveType.tieredRates || [],
            // Accrued balance (will be populated by backend)
            accruedBalance: leaveType.accruedBalance || 0,
          }));

          const updatedUser = {
            userLeaveSettings,
          };

          dispatch(users(updatedUser));
        }
        dispatch(setUserDataLoading(false));
      })
      .catch((error) => {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("onGetUser", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message || error?.message || "Request failed",
          });
        }
        Common.showErrorMessage(error);
        dispatch(setUserDataLoading(false));
      })
  }

};

export const onGetAnnualLeaveId = () => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = {};
  return new axiosBasic
    .get(LeaveType.GetCompanyAnnualLeaveTypeId)
    .then((response) => {
      const { success, message, data } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetAnnualLeaveId", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (message?.message || message),
        });
      }
      if (success) {
        return data;
      } else {
        Common.showErrorMessage(message.message);
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetAnnualLeaveId", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message?.errmsg || error?.message || "Request failed",
        });
      }
      Common.showErrorMessage(error.response.data.message.errmsg);
    });
};

export const onRegisterByAdmin = (values) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = safeUserInput(values);
  const additionalProfile = buildAdditionalProfilePayload(values.additionalProfile);
  const obj = {
    name: values.name,
    password: values.password,
    email: values.email,
    empStartDate: values.empStartDate,
    role: values.role,
    officeId: values.officeId,
    leaveYearStart: values.leaveYearStart,
    approvalId: values.approvalId,
    departmentId: values.departmentId,
    designationId: values.designationId ? String(values.designationId) : null,
    employmentTypeId: values.employmentTypeId ? String(values.employmentTypeId) : null,
    teamIds: buildTeamIdsPayload(values.teamIds),
    ...(additionalProfile ? { additionalProfile } : {}),
    imageUrl: values.imageUrl || "", // Add image URL field
    isLoginDisabled: values.isLoginDisabled !== undefined ? values.isLoginDisabled : false,
    userLeaveSettings: values.userLeaveSettings.map((leave) => {
      const activeValue = leave.active === true || leave.active === false ? leave.active : (leave.Active === true || leave.Active === false ? leave.Active : true);
      // Preserve the value even when inactive - don't set to 0, ensure integer for backend Int32
      const leaveValue = leave.value !== undefined && leave.value !== null && leave.value !== ""
        ? Math.round(Number(leave.value))  // Round to nearest integer for Int32 compatibility
        : 0;
      return {
        leaveTypeId: leave.leaveTypeId || leave.id, // Always send leaveTypeId
        leaveBalanceId: leave.leaveBalanceId, // Always send leaveBalanceId if present
        name: leave.name,
        value: leaveValue,
        type: leave.type || 'Days',
        active: activeValue, // Backend expects lowercase 'active'
        // Accrual settings - include if they exist
        accrualStartFrequency: leave.accrualStartFrequency || leave.accrualFrequency || 'Annually',
        accrualStartType: leave.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START,
        customAccrualRate: leave.customAccrualRate !== undefined && leave.customAccrualRate !== null ? parseFloat(leave.customAccrualRate) : 0,
        customAccrualFrequency: leave.customAccrualFrequency || leave.accrualFrequency || null,
        customAccrualCap: leave.customAccrualCap !== undefined && leave.customAccrualCap !== null ? parseFloat(leave.customAccrualCap) : 0,
        accrualEndDate: leave.accrualEndDate || null,
        enableAccrual: leave.enableAccrual !== undefined ? leave.enableAccrual : true,
        // Division fields for Accrual Rate and Accrual Cap
        // C# enum LeaveDivision: Days = 0, Hours = 1
        customAccrualRateDivision: (typeof leave.customAccrualRateDivision === 'number')
          ? leave.customAccrualRateDivision  // Already an integer (0 or 1)
          : ((typeof leave.customAccrualRateDivision === 'string' && (leave.customAccrualRateDivision === 'Hours' || leave.customAccrualRateDivision.toLowerCase() === 'hours'))
            ? 1  // Hours
            : 0), // Days (default)
        customAccrualCapDivision: (typeof leave.customAccrualCapDivision === 'number')
          ? leave.customAccrualCapDivision  // Already an integer (0 or 1)
          : ((typeof leave.customAccrualCapDivision === 'string' && (leave.customAccrualCapDivision === 'Hours' || leave.customAccrualCapDivision.toLowerCase() === 'hours'))
            ? 1  // Hours
            : 0), // Days (default)
        accrualRateDivision: (typeof leave.accrualRateDivision === 'number')
          ? leave.accrualRateDivision  // Already an integer (0 or 1)
          : ((typeof leave.accrualRateDivision === 'string' && (leave.accrualRateDivision === 'Hours' || leave.accrualRateDivision.toLowerCase() === 'hours'))
            ? 1  // Hours
            : 0), // Days (default)
        accrualCapDivision: (typeof leave.accrualCapDivision === 'number')
          ? leave.accrualCapDivision  // Already an integer (0 or 1)
          : ((typeof leave.accrualCapDivision === 'string' && (leave.accrualCapDivision === 'Hours' || leave.accrualCapDivision.toLowerCase() === 'hours'))
            ? 1  // Hours
            : 0), // Days (default)
      };
    }),
  };
  return axiosBasic
    .post(Account.RegisterByCompanyAdmin, obj)
    .then((response) => {
      const { success, message, data } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onRegisterByAdmin", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        Common.showSuccessMessage(message);
        // Return success and user ID for saving accrual settings
        return { success: true, userId: data?.id || data?.userId, message };
      } else {
        Common.showErrorMessage(message);
        return { success: false, message };
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onRegisterByAdmin", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message || error?.message || "Request failed",
        });
      }
      Common.showErrorMessage(error?.response);
      return { success: false, error: error?.response };
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};

export const onEditUser = (values, userId) => (dispatch) => {
  const currentUserId = getUserInfo()?.selfUserId ?? null;
  const userInput = { targetUserId: userId, ...safeUserInput(values) };
  const additionalProfile = buildAdditionalProfilePayload(values.additionalProfile);
  const obj = {
    id: userId,
    name: values.name,
    email: values.email,
    empStartDate: values.empStartDate,
    role: values.role,
    officeId: values.officeId,
    leaveYearStart: values.leaveYearStart,
    approvalId: values.approvalId,
    departmentId: values.departmentId,
    designationId: values.designationId ? String(values.designationId) : null,
    employmentTypeId: values.employmentTypeId ? String(values.employmentTypeId) : null,
    teamIds: buildTeamIdsPayload(values.teamIds),
    ...(additionalProfile ? { additionalProfile } : {}),
    imageUrl: values.imageUrl || "", // Add image URL field
    isLoginDisabled: values.isLoginDisabled !== undefined ? values.isLoginDisabled : false,
    userLeaveSettings: values.userLeaveSettings.map((leave) => {
      const activeValue = leave.active === true || leave.active === false ? leave.active : (leave.Active === true || leave.Active === false ? leave.Active : true);
      // Preserve the value even when inactive - don't set to 0
      const leaveValue = leave.value !== undefined && leave.value !== null ? Number(leave.value) : 0;
      return {
        leaveTypeId: leave.leaveTypeId || leave.id, // Always send leaveTypeId
        leaveBalanceId: leave.leaveBalanceId, // Always send leaveBalanceId if present
        name: leave.name,
        value: leaveValue,
        type: leave.type || 'Days',
        active: activeValue, // Backend expects lowercase 'active'
        // Accrual settings - include if they exist
        accrualStartFrequency: leave.accrualStartFrequency || leave.accrualFrequency || 'Annually',
        accrualStartType: leave.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START,
        customAccrualRate: leave.customAccrualRate !== undefined && leave.customAccrualRate !== null ? parseFloat(leave.customAccrualRate) : 0,
        customAccrualFrequency: leave.customAccrualFrequency || leave.accrualFrequency || null,
        customAccrualCap: leave.customAccrualCap !== undefined && leave.customAccrualCap !== null ? parseFloat(leave.customAccrualCap) : 0,
        accrualEndDate: leave.accrualEndDate || null,
        enableAccrual: leave.enableAccrual !== undefined ? leave.enableAccrual : true,
        // Division fields for Accrual Rate and Accrual Cap
        // C# enum LeaveDivision: Days = 0, Hours = 1
        customAccrualRateDivision: (typeof leave.customAccrualRateDivision === 'number')
          ? leave.customAccrualRateDivision  // Already an integer (0 or 1)
          : ((typeof leave.customAccrualRateDivision === 'string' && (leave.customAccrualRateDivision === 'Hours' || leave.customAccrualRateDivision.toLowerCase() === 'hours'))
            ? 1  // Hours
            : 0), // Days (default)
        customAccrualCapDivision: (typeof leave.customAccrualCapDivision === 'number')
          ? leave.customAccrualCapDivision  // Already an integer (0 or 1)
          : ((typeof leave.customAccrualCapDivision === 'string' && (leave.customAccrualCapDivision === 'Hours' || leave.customAccrualCapDivision.toLowerCase() === 'hours'))
            ? 1  // Hours
            : 0), // Days (default)
        accrualRateDivision: (typeof leave.accrualRateDivision === 'number')
          ? leave.accrualRateDivision  // Already an integer (0 or 1)
          : ((typeof leave.accrualRateDivision === 'string' && (leave.accrualRateDivision === 'Hours' || leave.accrualRateDivision.toLowerCase() === 'hours'))
            ? 1  // Hours
            : 0), // Days (default)
        accrualCapDivision: (typeof leave.accrualCapDivision === 'number')
          ? leave.accrualCapDivision  // Already an integer (0 or 1)
          : ((typeof leave.accrualCapDivision === 'string' && (leave.accrualCapDivision === 'Hours' || leave.accrualCapDivision.toLowerCase() === 'hours'))
            ? 1  // Hours
            : 0), // Days (default)
      };
    }),
  };

  return axiosBasic
    .post(AppUser.EditUser, obj)
    .then((response) => {
      const { success, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onEditUser", {
          type: success ? "info" : "exception",
          userId: currentUserId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        Common.showSuccessMessage(message || "Updated");
        return { success: true, message };
      } else {
        Common.showErrorMessage(message);
        return { success: false, message };
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onEditUser", {
          type: "exception",
          userId: currentUserId,
          success: false,
          userInput,
          reason: error?.response?.data?.message || error?.message || "Request failed",
        });
      }
      Common.showErrorMessage(error?.response);
      return { success: false, error: error?.response };
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};


export const roles = (roles) => ({
  type: ADMIN_ROLES,
  payload: roles,
});

export const onGetAdminRoles = () => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = {};
  dispatch(isLoading(true));
  axiosBasic
    .get(Account.GetAdminRoles)
    .then((response) => {
      const { success, data, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetAdminRoles", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        const userRoles = [];
        userRoles.push(
          data.map((i) => ({ key: i.id, text: i.name, value: i.id }))
        );
        dispatch(roles(userRoles));
      } else {
        Common.showErrorMessage(message);
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetAdminRoles", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message || error?.message || "Request failed",
        });
      }
      Common.showErrorMessage(error);
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};

export const offices = (offices) => ({
  type: OFFICES,
  payload: offices,
});

export const onGetOffices = (disableLoading) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { disableLoading };
  if (!disableLoading) {
    dispatch(isLoading(true));
  }
  axiosBasic
    .get(Office.GetCompanyOffices)
    .then((response) => {
      const { success, data, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetOffices", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {

        const companyOffices = [];
        companyOffices.push(
          data.map((i) => ({
            key: i.id,
            text: i.countryName ? `${i.name} (${i.countryName})` : i.name,
            value: i.id,
          }))
        );
        dispatch(offices(companyOffices));
      } else {
        Common.showErrorMessage(message);
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetOffices", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message || error?.message || "Request failed",
        });
      }
      Common.showErrorMessage(error);
    })
    .finally(() => {
      if (!disableLoading) {
        dispatch(isLoading(false));
      }
    });
};

export const departments = (departments) => ({
  type: DEPARTMENTS,
  payload: departments,
});

export const onGetDepartments = (disableLoading) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { disableLoading };
  if (!disableLoading) {
    dispatch(isLoading(true));
  }
  axiosBasic
    .get(Department.Get)
    .then((response) => {
      const { success, data, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetDepartments", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        const companyDepartments = [];
        companyDepartments.push(
          data.map((i) => ({ key: i.id, text: i.name, value: i.id, isActive: i.isActive !== undefined ? i.isActive : true }))
        );
        dispatch(departments(companyDepartments));
      } else {
        Common.showErrorMessage(message);
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetDepartments", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message || error?.message || "Request failed",
        });
      }
      Common.showErrorMessage(error);
    })
    .finally(() => {
      if (!disableLoading) {
        dispatch(isLoading(false));
      }
    });
};

export const companyApprovers = (approvers) => ({
  type: APPROVERS,
  payload: approvers,
});

export const onGetApprovers = (userIdParam = null) => (dispatch) => {
  const currentUserId = getUserInfo()?.selfUserId ?? null;
  const userInput = { userId: userIdParam };
  dispatch(isLoading(true));
  const queryParam = userIdParam ? `?userId=${userIdParam}` : '';
  axiosBasic
    .get(`${Account.onGetApprovers}${queryParam}`)
    .then((response) => {
      const { success, data, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetApprovers", {
          type: success ? "info" : "exception",
          userId: currentUserId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        const approvers = [];
        approvers.push(
          data.map((i) => ({ key: i.id, text: i.name + ' ' + i.email, value: i.id }))
        );
        dispatch(companyApprovers(approvers));
      } else {
        Common.showErrorMessage(message);
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetApprovers", {
          type: "exception",
          userId: currentUserId,
          success: false,
          userInput,
          reason: error?.response?.data?.message || error?.message || "Request failed",
        });
      }
      Common.showErrorMessage(error);
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};

export const toggleUserStatus = (userId, isActive) => ({
  type: TOGGLE_USER_STATUS,
  payload: { userId, isActive },
});

export const onToggleUserStatus = (userId, isActive) => (dispatch) => {
  const currentUserId = getUserInfo()?.selfUserId ?? null;
  const userInput = { targetUserId: userId, isActive };
  dispatch(isLoading(true));
  return new axiosBasic
    .post(`${AppUser.ToggleUserStatus}?userId=${userId}&isActive=${isActive}`)
    .then((response) => {
      const { success, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onToggleUserStatus", {
          type: success ? "info" : "exception",
          userId: currentUserId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        dispatch(toggleUserStatus(userId, isActive));
        Common.showSuccessMessage(`User ${isActive ? 'activated' : 'deactivated'} successfully`);
      } else {
        Common.showErrorMessage(message);
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onToggleUserStatus", {
          type: "exception",
          userId: currentUserId,
          success: false,
          userInput,
          reason: error?.response?.data?.message || error?.message || "Request failed",
        });
      }
      Common.showErrorMessage(error?.response?.data?.message || 'Failed to update user status');
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};

export const updateProfileImage = (userId, imageUrl) => ({
  type: UPDATE_PROFILE_IMAGE,
  payload: { userId, imageUrl },
});

export const onUpdateProfileImage = (userId, imageUrl) => (dispatch) => {
  const currentUserId = getUserInfo()?.selfUserId ?? null;
  const userInput = { targetUserId: userId, hasImageUrl: !!(imageUrl && String(imageUrl).trim()) };
  dispatch(isLoading(true));
  return new axiosBasic
    .put(`${Account.UpdateAnyProfileImage}`, {
      id: userId,
      imageUrl: imageUrl
    })
    .then((response) => {
      const { success, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onUpdateProfileImage", {
          type: success ? "info" : "exception",
          userId: currentUserId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        dispatch(updateProfileImage(userId, imageUrl));
        Common.showSuccessMessage('Profile image updated successfully');
      } else {
        Common.showErrorMessage(message);
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onUpdateProfileImage", {
          type: "exception",
          userId: currentUserId,
          success: false,
          userInput,
          reason: error?.response?.data?.message || error?.message || "Request failed",
        });
      }
      Common.showErrorMessage(error?.response?.data?.message || 'Failed to update profile image');
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};

// Get custom accrual settings for a user and array of leave types
export const onGetAccrualSettings = (userId, leaveTypeIds) => (dispatch) => {
  const currentUserId = getUserInfo()?.selfUserId ?? null;
  const idsArray = Array.isArray(leaveTypeIds) ? leaveTypeIds : [leaveTypeIds];
  const userInput = { targetUserId: userId, leaveTypeIdsCount: idsArray.length };
  // Build query string with array of leaveTypeIds
  const queryParams = idsArray.map(id => `leaveTypeIds=${id}`).join('&');

  return axiosBasic
    .get(`${LeaveAccrual.GetAccrualSettings}?userId=${userId}&${queryParams}`)
    .then((response) => {
      const { success, data } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetAccrualSettings", {
          type: success ? "info" : "exception",
          userId: currentUserId,
          success: !!(success && data),
          userInput,
        });
      }
      if (success && data) {
        // If data is an array, return it; if single object, return as array
        const settingsArray = Array.isArray(data) ? data : [data];
        return { success: true, data: settingsArray };
      }
      return { success: false, data: [] };
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetAccrualSettings", {
          type: "exception",
          userId: currentUserId,
          success: false,
          userInput,
          reason: error?.response?.data?.message || error?.message || "Request failed",
        });
      }
      return { success: false, data: [] };
    });
};

// Save custom accrual settings for a user (create or update)
export const onSaveAccrualSettings = (settings, isUpdate = false) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { isUpdate, settingsCount: Array.isArray(settings) ? settings.length : (settings ? 1 : 0) };
  dispatch(isLoading(true));

  // Use PUT for update, POST for create
  const apiCall = isUpdate
    ? axiosBasic.put(LeaveAccrual.SaveAccrualSettings, settings)
    : axiosBasic.post(LeaveAccrual.SaveAccrualSettings, settings);

  return apiCall
    .then((response) => {
      const { success, message, data } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onSaveAccrualSettings", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        Common.showSuccessMessage(message || `Accrual settings ${isUpdate ? 'updated' : 'saved'} successfully`);
        return { success: true, data };
      } else {
        Common.showErrorMessage(message || 'Failed to save accrual settings');
        return { success: false, error: message };
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onSaveAccrualSettings", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message || error?.message || "Request failed",
        });
      }
      Common.showErrorMessage(error?.response?.data?.message || 'Failed to save accrual settings');
      return { success: false, error: error?.response?.data?.message };
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};

// Download bulk upload template
export const onDownloadBulkUploadTemplate = () => () => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = {};
  return axiosBasic
    .get(AppUser.BulkUploadTemplate, {
      responseType: 'blob' // Important for file downloads
    })
    .then((response) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onDownloadBulkUploadTemplate", { type: "info", userId, success: true, userInput });
      }
      // Create a blob from the response
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'UserBulkUploadTemplate.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
      Common.showSuccessMessage('Template downloaded successfully');
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onDownloadBulkUploadTemplate", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message || error?.message || "Request failed",
        });
      }
      Common.showErrorMessage(error?.response?.data?.message || 'Failed to download template');
    });
};

// Upload bulk upload file
export const onBulkUploadUsers = (file, params) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { fileName: file?.name, hasParams: !!params, page: params?.page };
  dispatch(isLoading(true));
  const formData = new FormData();
  formData.append('file', file);

  return axiosBasic
    .post(AppUser.BulkUpload, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((response) => {
      const { success, message, data } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onBulkUploadUsers", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        Common.showSuccessMessage(message || 'Users uploaded successfully');
        // Refresh users list after successful upload
        if (params) {
          dispatch(onGetUsers(params, params.page || 1));
        }
        return { success: true, message, data };
      } else {
        Common.showErrorMessage(message || 'Failed to upload users');
        return { success: false, message, errors: data?.errors || [] };
      }
    })
    .catch((error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to upload users';
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onBulkUploadUsers", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: errorMessage,
        });
      }
      Common.showErrorMessage(errorMessage);
      return {
        success: false,
        error: errorMessage,
        errors: error?.response?.data?.errors || []
      };
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};

// Delete user permanently
export const onDeleteUser = (userId, params) => (dispatch) => {
  const currentUserId = getUserInfo()?.selfUserId ?? null;
  const userInput = { targetUserId: userId, hasParams: !!params, page: params?.page };
  dispatch(isLoading(true));
  return new axiosBasic
    .delete(`${AppUser.Delete}?userId=${userId}`)
    .then((response) => {
      const { success, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onDeleteUser", {
          type: success ? "info" : "exception",
          userId: currentUserId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        Common.showSuccessMessage(message || 'User deleted permanently');
        // Refresh users list after successful deletion
        if (params) {
          if (params.searchText && params.searchText.trim() !== '') {
            dispatch(onSearchUsers(params, params.page || 1));
          } else {
            dispatch(onGetUsers(params, params.page || 1));
          }
        }
        return { success: true, message };
      } else {
        Common.showErrorMessage(message || 'Failed to delete user');
        return { success: false, message };
      }
    })
    .catch((error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete user';
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onDeleteUser", {
          type: "exception",
          userId: currentUserId,
          success: false,
          userInput,
          reason: errorMessage,
        });
      }
      Common.showErrorMessage(errorMessage);
      return { success: false, error: errorMessage };
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};